import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Card from "@react/shared/ui/Card"

const fmt = m => (m && m.formatted) || "—"
const pct = v => (v == null || isNaN(v)) ? "—" : `${Math.round(Number(v))} %`

const KpiCard = ({ label, value, sub, icon, tooltip, href, tone = "text-slate-900" }) => (
  <Card padding="md" className={`rounded-lg ${href ? "hover:border-brand-300 hover:shadow-sm transition" : ""}`}>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <span>{label}</span>
        {tooltip && (
          <span className="group/tip relative inline-flex">
            <FontAwesomeIcon
              icon      = "circle-info"
              className = "text-slate-400 hover:text-slate-600 text-[11px] cursor-help"
            />
            <span
              role      = "tooltip"
              className = "pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 px-2.5 py-1.5 bg-slate-900 text-white text-[11px] leading-snug rounded-md shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity z-30 text-center"
            >
              {tooltip}
            </span>
          </span>
        )}
      </div>
      {icon && <FontAwesomeIcon icon={icon} className="text-slate-400 text-[13px]" />}
    </div>
    <div className={`text-2xl font-semibold tabular-nums mt-2 ${href ? "text-brand-600" : tone}`}>
      {href ? (
        <a href={href} className="hover:text-brand-700 hover:underline">{value}</a>
      ) : value}
    </div>
    {sub && <div className="text-[11px] text-slate-500 mt-1">{sub}</div>}
  </Card>
)

const Alert = ({ children }) => (
  <Card padding="none" className="rounded-lg bg-amber-50 border-amber-200">
    <div className="flex items-start gap-3 px-4 py-3">
      <FontAwesomeIcon icon="triangle-exclamation" className="text-amber-600 text-[14px] mt-0.5" />
      <div className="text-sm text-amber-800">{children}</div>
    </div>
  </Card>
)

const KpiStrip = ({ kpis }) => {
  if (!kpis) return null
  const remainingTone = (kpis.remaining_to_bill?.cents || 0) < 0 ? "text-rose-600" : "text-slate-900"
  const marginTone    = (kpis.margin?.cents || 0) >= 0 ? "text-emerald-600" : "text-rose-600"

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.engaged",  { defaultValue: "Engagé" })}
          icon    = "file-invoice"
          value   = {fmt(kpis.engaged)}
          sub     = "Lignes facturables BI"
          tooltip = "Total des lignes d'articles sur toutes les interventions non annulées du marché. Valeur engagée vis-à-vis du client."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.invoiced", { defaultValue: "Facturé" })}
          icon    = "receipt"
          value   = {fmt(kpis.invoiced)}
          sub     = "Factures confirmées"
          tooltip = "Montant déjà émis sur factures (statut Confirmé ou Payé) liées à ce marché."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.remaining_to_bill", { defaultValue: "Restant à facturer" })}
          icon    = "hourglass-half"
          value   = {fmt(kpis.remaining_to_bill)}
          tone    = {remainingTone}
          sub     = "Engagé − Facturé"
          tooltip = "Différence entre l'engagé et le facturé. Positif = reste à émettre, négatif = sur-facturé."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.cost", { defaultValue: "Coût engagé" })}
          icon    = "money-bill-trend-up"
          value   = {fmt(kpis.cost)}
          sub     = "Snapshot BPU"
          tooltip = "Coût d'achat estimé des articles posés, basé sur le prix d'achat figé au BPU au moment de la pose."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.margin", { defaultValue: "Marge" })}
          icon    = "chart-line"
          value   = {fmt(kpis.margin)}
          tone    = {marginTone}
          sub     = {kpis.margin_pct != null ? `${pct(kpis.margin_pct)} de marge` : "—"}
          tooltip = "Marge brute = Engagé − Coût engagé. Le pourcentage indique la marge rapportée à l'engagé."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.bpu_compliance_pct", { defaultValue: "Conformité BPU" })}
          icon    = "shield-halved"
          value   = {pct(kpis.bpu_compliance_pct)}
          sub     = {kpis.bpu_compliance_pct != null ? `${fmt(kpis.bpu_revenue)} sur BPU` : "—"}
          tooltip = "Part du CA réalisée sur des articles du BPU par rapport au total (BPU + hors BPU). Mesure l'adhérence au catalogue."
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label   = {I18n.t("navigation.workorders", { defaultValue: "Interventions" })}
          icon    = "screwdriver-wrench"
          value   = {kpis.workorders_count ?? 0}
          sub     = {`${kpis.workorders_open ?? 0} ouvertes`}
          href    = {kpis.workorders_url}
          tooltip = "Nombre total d'interventions rattachées au marché. La sous-ligne montre celles encore non terminées."
        />
        <KpiCard
          label   = {I18n.t("navigation.quotes", { defaultValue: "Devis" })}
          icon    = "file-lines"
          value   = {kpis.quotes_count ?? 0}
          sub     = {`${kpis.quotes_signed ?? 0} signés`}
          href    = {kpis.quotes_url}
          tooltip = "Nombre total de devis rattachés au marché. La sous-ligne montre les devis acceptés ou facturés."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.paid", { defaultValue: "Réglé" })}
          icon    = "circle-check"
          value   = {fmt(kpis.paid)}
          tooltip = "Montant des factures déjà encaissées (statut Payé) pour ce marché."
        />
        <KpiCard
          label   = {I18n.t("mbc_contracts.synthese.days_remaining", { defaultValue: "Jours restants" })}
          icon    = "calendar-day"
          value   = {kpis.days_remaining ?? "—"}
          tooltip = "Jours restants avant la date de fin du marché. Vide si aucune date de fin n'est définie."
        />
      </div>

      {kpis.reconciliation_needed && (
        <Alert>
          {I18n.t("mbc_contracts.synthese.cost_reconciliation_needed", {
            defaultValue: "Écart détecté avec les lignes de coût (>5%)"
          })}
        </Alert>
      )}
    </>
  )
}

export default KpiStrip
